import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../api/apiClient';
import { getDemoPortfolio, monthlyBuyDates } from '../data/demoPortfolios';
import PortfolioHero from '../features/portfolio/components/PortfolioHero';
import PortfolioChart from '../features/portfolio/components/PortfolioChart';
import HoldingsTable from '../features/portfolio/components/HoldingsTable';
import PortfolioAllocationChart from '../features/portfolio/components/PortfolioAllocationChart';
import HoldingTransactionsTable from '../features/portfolio/components/HoldingTransactionsTable';

const noop = () => {};

// Per-ticker logo overrides for symbols Parqet doesn't cover (crypto, gold,
// some EU listings). CoinGecko's CDN serves stable PNGs at predictable IDs.
const LOGO_OVERRIDES = {
  BTC:  'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
  ETH:  'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
  GOLD: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Gold-crystals.jpg/240px-Gold-crystals.jpg',
};

function resolveLogoUrl(ticker) {
  if (LOGO_OVERRIDES[ticker]) return LOGO_OVERRIDES[ticker];
  return `https://assets.parqet.com/logos/symbol/${ticker}?format=jpg`;
}

// Demo portfolios are always single-currency (holdings.currency == base
// currency), so the "Portfolio" duplicates of Current Value and P/L are
// identical to the local versions and just waste space.
const DEMO_OPEN_COLS = [
  'image', 'name', 'ticker', 'quantity', 'weight', 'avgBuy',
  'currentPrice', 'currentValue',
  'dividendYield', 'yieldOnCost',
  'plLocal', 'transactions',
];
const DEMO_CLOSED_COLS = [
  'image', 'name', 'type', 'ticker', 'quantity',
  'avgBuy', 'avgSell', 'currentPrice',
  'realizedPlLocal', 'transactions',
];
const asyncNoop = async () => {};

// Same palette as PortfolioAllocationChart's SLICE_COLORS, so allocation slices
// and chart buy markers share colours.
const PALETTE = [
  '#f472b6', '#c084fc', '#60a5fa', '#14b8a6', '#fde047', '#fb7185',
  '#818cf8', '#e879f9', '#a78bfa', '#2dd4bf', '#f9a8d4', '#93c5fd',
];

/**
 * Read-only demo portfolio for non-authenticated visitors. Reuses the real
 * dashboard components (hero, chart, holdings, allocation, transactions) but
 * feeds them static holdings + live Yahoo prices — exactly what a free-tier
 * user sees, minus the editing forms.
 */
export default function DemoPortfolioDetailPage() {
  const { demoId } = useParams();
  const portfolio = getDemoPortfolio(demoId);

  const [quotes, setQuotes] = useState({});       // yahoo -> { currentPrice, previousClose }
  const [histories, setHistories] = useState({}); // yahoo -> Map(date -> close)  (full 'all' range)
  const [benchmarkAll, setBenchmarkAll] = useState([]); // SPY full history
  const [range, setRange] = useState('all');
  const [dataLoading, setDataLoading] = useState(true);

  const BENCHMARK_TICKER = 'SPY';
  const cur = portfolio?.currency || 'USD';

  /* ── Live current prices (once) ── */
  useEffect(() => {
    if (!portfolio) return;
    let cancelled = false;
    const tickers = portfolio.holdings.map(h => h.yahoo).join(',');
    apiFetch(`/api/stock/quotes?tickers=${encodeURIComponent(tickers)}`)
      .then(r => r.ok ? r.json() : {})
      .then(data => { if (!cancelled) setQuotes(data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [demoId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Full ('all') history per holding + benchmark, fetched once ── */
  useEffect(() => {
    if (!portfolio) return;
    let cancelled = false;
    setDataLoading(true);
    const toMap = (arr) => {
      const m = new Map();
      (Array.isArray(arr) ? arr : []).forEach(pt => {
        const d = String(pt.date ?? pt.Date ?? '').slice(0, 10);
        const c = Number(pt.close ?? pt.Close ?? pt.price ?? pt.Price ?? 0);
        if (d && c > 0) m.set(d, c);
      });
      return m;
    };
    Promise.allSettled([
      ...portfolio.holdings.map(h =>
        apiFetch(`/api/stock/history/${encodeURIComponent(h.yahoo)}?range=all`)
          .then(r => r.ok ? r.json() : [])
          .then(data => ['h:' + h.yahoo, data])
      ),
      apiFetch(`/api/stock/history/${BENCHMARK_TICKER}?range=all`)
        .then(r => r.ok ? r.json() : [])
        .then(data => ['spy', data]),
    ]).then(results => {
      if (cancelled) return;
      const hmap = {};
      let spy = [];
      results.forEach(res => {
        if (res.status !== 'fulfilled' || !res.value) return;
        const [key, data] = res.value;
        if (key === 'spy') spy = Array.isArray(data) ? data : [];
        else hmap[key.slice(2)] = toMap(data);
      });
      setHistories(hmap);
      setBenchmarkAll(spy);
    }).finally(() => { if (!cancelled) setDataLoading(false); });
    return () => { cancelled = true; };
  }, [demoId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nearest close on/just-before a date, walking back from the given day.
  const closeOnOrBefore = (map, dateStr) => {
    if (!map) return null;
    if (map.has(dateStr)) return map.get(dateStr);
    const keys = Array.from(map.keys());
    let best = null;
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] <= dateStr && (best === null || keys[i] > best)) best = keys[i];
    }
    return best != null ? map.get(best) : null;
  };

  /* ── Buy lots: monthly DCA (optional, with cutoff) + explicit lump buys ── */
  const lotsByTicker = useMemo(() => {
    const out = {};
    if (!portfolio) return out;
    portfolio.holdings.forEach(h => {
      const map = histories[h.yahoo];
      const lots = [];

      // Monthly DCA (when configured), skipping months where price ≥ cutoff
      if (h.startDate && h.sharesPerBuy) {
        monthlyBuyDates(h.startDate).forEach(date => {
          const price = closeOnOrBefore(map, date);
          if (!price) return;
          if (h.dcaCutoffPrice && price >= h.dcaCutoffPrice) return;
          lots.push({ date, qty: h.sharesPerBuy, price });
        });
      }

      // Explicit lump buys from the actions list
      (h.actions || []).forEach(a => {
        if (a.type !== 'buy') return;
        const price = closeOnOrBefore(map, a.date);
        if (price) lots.push({ date: a.date, qty: a.qty, price });
      });

      lots.sort((a, b) => a.date.localeCompare(b.date));
      out[h.ticker] = lots;
    });
    return out;
  }, [portfolio, histories]);

  /* ── Combined chronological events (buy + sell) per ticker.
       Resolves qty: 'all' to the actual share count at the sell date by
       walking events in order — so consumers don't have to deal with it. ── */
  const eventsByTicker = useMemo(() => {
    const out = {};
    if (!portfolio) return out;
    portfolio.holdings.forEach(h => {
      const map = histories[h.yahoo];
      const buys = (lotsByTicker[h.ticker] || []).map(l => ({ ...l, type: 'buy' }));
      const rawSells = (h.actions || []).filter(a => a.type === 'sell');

      const merged = [
        ...buys,
        ...rawSells.map(a => ({ date: a.date, rawQty: a.qty, type: 'sell' })),
      ].sort((a, b) => a.date.localeCompare(b.date) || (a.type === 'buy' ? -1 : 1));

      let cumQty = 0;
      const resolved = [];
      merged.forEach(ev => {
        if (ev.type === 'buy') {
          cumQty += ev.qty;
          resolved.push(ev);
        } else {
          const price = closeOnOrBefore(map, ev.date);
          if (!price) return;
          const qty = ev.rawQty === 'all' ? cumQty : Math.min(ev.rawQty, cumQty);
          if (qty <= 0) return;
          cumQty -= qty;
          resolved.push({ date: ev.date, qty, price, type: 'sell' });
        }
      });
      out[h.ticker] = resolved;
    });
    return out;
  }, [portfolio, histories, lotsByTicker]);

  /* ── Holdings shaped for the real components ── */
  const holdings = useMemo(() => {
    if (!portfolio) return [];
    const priceOf = (h) => {
      const q = quotes[h.yahoo];
      const map = histories[h.yahoo];
      let lastClose = null;
      if (map && map.size) {
        const keys = Array.from(map.keys()).sort();
        lastClose = map.get(keys[keys.length - 1]);
      }
      return Number(q?.currentPrice) || lastClose || null;
    };
    const enriched = portfolio.holdings.map(h => {
      // Walk events chronologically: each buy adds qty/cost, each sell removes
      // qty proportionally from open cost basis and books realised P/L.
      let qty = 0, costBasis = 0, realisedPnl = 0;
      let totalSoldQty = 0, totalSoldRevenue = 0;
      let totalBoughtQty = 0, lifetimeBoughtCost = 0;
      (eventsByTicker[h.ticker] || []).forEach(ev => {
        if (ev.type === 'buy') {
          qty += ev.qty;
          costBasis += ev.qty * ev.price;
          totalBoughtQty += ev.qty;
          lifetimeBoughtCost += ev.qty * ev.price;
        } else {
          const sellQty = Math.min(ev.qty, qty);
          const avg = qty > 0 ? costBasis / qty : 0;
          realisedPnl += sellQty * (ev.price - avg);
          costBasis -= sellQty * avg;
          qty -= sellQty;
          totalSoldQty += sellQty;
          totalSoldRevenue += sellQty * ev.price;
        }
      });
      const averageSellPrice = totalSoldQty > 0 ? totalSoldRevenue / totalSoldQty : 0;
      const lifetimeAvgBuy = totalBoughtQty > 0 ? lifetimeBoughtCost / totalBoughtQty : 0;
      const events = eventsByTicker[h.ticker] || [];
      const sellsCount = events.filter(e => e.type === 'sell').length;
      const buysCount  = events.filter(e => e.type === 'buy').length;
      // For open positions show the current open avg cost; for closed positions
      // (qty == 0) fall back to the lifetime avg buy so the Closed table shows
      // meaningful Avg Buy and Realized P/L instead of zeros.
      const avgCost = qty > 0 ? costBasis / qty : lifetimeAvgBuy;
      const currentPrice = priceOf(h) || avgCost;
      // Yield on cost — annualised dividend yield projected over the average
      // cost actually paid for open shares.
      const yieldOnCost = (qty > 0 && avgCost > 0 && h.dividendYield)
        ? h.dividendYield * (currentPrice / avgCost)
        : null;
      const annualDividendPerShare = h.dividendYield
        ? (currentPrice * h.dividendYield) / 100
        : null;
      const marketValue = qty * currentPrice;
      const pnl = marketValue - costBasis;
      const pnlPct = costBasis > 0 ? (pnl / costBasis) * 100 : 0;
      const annualDividendTotal = h.dividendYield ? marketValue * (h.dividendYield / 100) : 0;
      return {
        ticker: h.ticker,
        displayName: h.name,
        assetType: h.assetType,
        currency: cur,
        instrumentId: `demo-${h.ticker}`,
        logoUrl: resolveLogoUrl(h.ticker),
        openQuantity: qty,
        quantity: qty,
        averageBuyPrice: avgCost,
        averageCostPerUnit: avgCost,
        currentPrice,
        marketValueNative: marketValue,
        marketValueBase: marketValue,
        costBasisBase: costBasis,
        costBasisOpenBase: costBasis,
        costBasisOpenNative: costBasis,
        unrealizedPnlNative: pnl,
        unrealizedPnlBase: pnl,
        unrealizedPnlPercent: pnlPct,
        totalPnlBase: pnl + realisedPnl,
        realizedPnlBase: realisedPnl,
        realizedPnlNative: realisedPnl, // demo currency == base currency, so native == base
        dividendIncomeBase: 0,
        dividendYield: h.dividendYield || 0,
        annualDividendPerShare,
        annualDividendTotal,
        yieldOnCost,
        sellTransactions: sellsCount,
        buyTransactions: buysCount,
        // For closed-position view in HoldingsTable (isClosed mode)
        soldQuantity: totalSoldQty,
        averageSellPrice,
        // Distinguish fully-closed positions: bought stuff but nothing left.
        _isFullyClosed: totalSoldQty > 0 && qty < 1e-6,
      };
    });
    const totalValue = enriched.reduce((s, h) => s + h.marketValueBase, 0);
    enriched.forEach(h => {
      h.portfolioSharePercent = totalValue > 0 ? (h.marketValueBase / totalValue) * 100 : 0;
    });
    return enriched.sort((a, b) => b.marketValueBase - a.marketValueBase);
  }, [portfolio, quotes, histories, eventsByTicker, cur]);

  // Open vs fully-closed positions (closed = bought and sold everything)
  const openHoldings = useMemo(
    () => holdings.filter(h => !h._isFullyClosed),
    [holdings]
  );
  const closedHoldings = useMemo(
    () => holdings.filter(h => h._isFullyClosed),
    [holdings]
  );

  // Ticker → colour, in market-value order (matches allocation slice colours).
  // Only open positions get a colour — closed ones don't appear in the pie.
  const tickerColorMap = useMemo(() => {
    const m = new Map();
    openHoldings.forEach((h, i) => m.set(h.ticker.toUpperCase(), PALETTE[i % PALETTE.length]));
    return m;
  }, [openHoldings]);

  // Ticker → annual dividend (base) for the allocation "Dividends" pie
  const dividendsByTicker = useMemo(() => {
    const map = {};
    holdings.forEach(h => {
      if (h.annualDividendTotal > 0) map[h.ticker] = h.annualDividendTotal;
    });
    return map;
  }, [holdings]);

  /* ── Buy + sell transactions ── */
  const transactions = useMemo(() => {
    if (!portfolio) return [];
    const txs = [];
    portfolio.holdings.forEach(h => {
      (eventsByTicker[h.ticker] || []).forEach((ev, i) => {
        const gross = ev.qty * ev.price;
        txs.push({
          id: `demo-tx-${h.ticker}-${i}`,
          transactionType: ev.type === 'buy' ? 0 : 1,
          ticker: h.ticker,
          instrument: { ticker: h.ticker, displayName: h.name },
          quantity: ev.qty,
          pricePerUnit: ev.price,
          tradeCurrency: cur,
          feeAmount: 0,
          grossAmount: gross,
          netAmount: gross,
          occurredAt: new Date(ev.date + 'T12:00:00').toISOString(),
          notes: ev.type === 'buy' ? 'Monthly buy' : 'Strategic sell',
        });
      });
    });
    return txs;
  }, [portfolio, eventsByTicker, cur]);

  // Earliest buy date across the whole portfolio (for the "Since buy" range).
  const firstBuyDate = useMemo(() => {
    let earliest = null;
    Object.values(lotsByTicker).forEach(lots => {
      if (lots.length && (earliest === null || lots[0].date < earliest)) earliest = lots[0].date;
    });
    return earliest;
  }, [lotsByTicker]);

  const priceByTicker = useMemo(() => {
    const map = {};
    holdings.forEach(h => { map[h.ticker] = h.currentPrice; });
    return map;
  }, [holdings]);

  // Window start date for the currently selected range.
  const rangeStartDate = useMemo(() => {
    if (range === 'all') return firstBuyDate || '0000-01-01';
    const now = new Date();
    const d = new Date(now);
    switch (range) {
      case '1d':  d.setDate(now.getDate() - 1); break;
      case '7d':  d.setDate(now.getDate() - 7); break;
      case '30d': d.setMonth(now.getMonth() - 1); break;
      case '90d': d.setMonth(now.getMonth() - 3); break;
      case '180d':d.setMonth(now.getMonth() - 6); break;
      case 'ytd': return `${now.getFullYear()}-01-01`;
      case '1y':  d.setFullYear(now.getFullYear() - 1); break;
      case '3y':  d.setFullYear(now.getFullYear() - 3); break;
      case '5y':  d.setFullYear(now.getFullYear() - 5); break;
      default: return firstBuyDate || '0000-01-01';
    }
    const iso = d.toISOString().slice(0, 10);
    // Never show before the first buy — the portfolio didn't exist yet.
    return firstBuyDate && iso < firstBuyDate ? firstBuyDate : iso;
  }, [range, firstBuyDate]);

  /* ── Chart points: cumulative DCA value vs invested, per day ── */
  const points = useMemo(() => {
    if (!portfolio || !firstBuyDate) return [];
    // Union of all trading days from the full history maps.
    const allDates = new Set();
    Object.values(histories).forEach(m => m && m.forEach((_, d) => allDates.add(d)));
    const dates = Array.from(allDates).sort().filter(d => d >= firstBuyDate);

    // Walk combined buy + sell events chronologically.
    const lastClose = {};
    const evIdx = {};
    const cumQty = {};
    const cumCost = {};
    portfolio.holdings.forEach(h => { evIdx[h.ticker] = 0; cumQty[h.ticker] = 0; cumCost[h.ticker] = 0; });

    const series = dates.map(date => {
      let holdingsValue = 0;
      let invested = 0;
      portfolio.holdings.forEach(h => {
        const events = eventsByTicker[h.ticker] || [];
        while (evIdx[h.ticker] < events.length && events[evIdx[h.ticker]].date <= date) {
          const ev = events[evIdx[h.ticker]];
          if (ev.type === 'buy') {
            cumQty[h.ticker] += ev.qty;
            cumCost[h.ticker] += ev.qty * ev.price;
          } else {
            const sellQty = Math.min(ev.qty, cumQty[h.ticker]);
            const avg = cumQty[h.ticker] > 0 ? cumCost[h.ticker] / cumQty[h.ticker] : 0;
            cumCost[h.ticker] -= sellQty * avg;
            cumQty[h.ticker]  -= sellQty;
          }
          evIdx[h.ticker] += 1;
        }
        invested += cumCost[h.ticker];
        const m = histories[h.yahoo];
        let c = m?.get(date);
        if (c == null) c = lastClose[h.yahoo];
        if (c != null) { lastClose[h.yahoo] = c; holdingsValue += cumQty[h.ticker] * c; }
      });
      return {
        date,
        totalValueBase: holdingsValue,
        holdingsValueBase: holdingsValue,
        cashValueBase: 0,
        netContributionsBase: invested,
        performancePnlBase: holdingsValue - invested,
      };
    }).filter(p => p.holdingsValueBase > 0);

    // Slice to the selected range window.
    return series.filter(p => p.date >= rangeStartDate);
  }, [portfolio, histories, eventsByTicker, firstBuyDate, rangeStartDate]);

  // Benchmark sliced to the same window.
  const benchmarkPoints = useMemo(
    () => (Array.isArray(benchmarkAll) ? benchmarkAll : []).filter(pt => {
      const d = String(pt.date ?? pt.Date ?? '').slice(0, 10);
      return d >= rangeStartDate;
    }),
    [benchmarkAll, rangeStartDate]
  );

  /* ── Hero summary ── */
  const summary = useMemo(() => {
    const holdingsValue = holdings.reduce((s, h) => s + h.marketValueBase, 0);
    const invested = holdings.reduce((s, h) => s + h.costBasisBase, 0);
    const unrealized = holdingsValue - invested;
    const annualDiv = holdings.reduce((s, h) => s + (h.annualDividendTotal || 0), 0);

    // All-time dividends: accrue each monthly lot's dividends from its buy date
    // to today (qty × buy price × yield% × years held).
    const now = Date.now();
    let dividendAllTime = 0;
    (portfolio?.holdings || []).forEach(h => {
      if (!h.dividendYield) return;
      (lotsByTicker[h.ticker] || []).forEach(lot => {
        const years = Math.max(0, (now - new Date(lot.date).getTime()) / (365.25 * 864e5));
        dividendAllTime += lot.qty * lot.price * (h.dividendYield / 100) * years;
      });
    });

    return {
      holdingsValue,
      invested,
      unrealized,
      realized: holdings.reduce((s, h) => s + (h.realizedPnlBase || 0), 0),
      annualDiv,
      dividendAllTime,
      dividendYield: holdingsValue > 0 ? (annualDiv / holdingsValue) * 100 : 0,
      yieldOnCost: invested > 0 ? (annualDiv / invested) * 100 : 0,
    };
  }, [holdings, portfolio, lotsByTicker]);

  // Cache the computed snapshot so the Portfolios list cards can show the
  // exact same numbers when the visitor goes back, instead of placeholders.
  useEffect(() => {
    if (!portfolio || dataLoading || !summary.holdingsValue) return;
    try {
      sessionStorage.setItem(`demo-stats-${demoId}`, JSON.stringify({
        value: summary.holdingsValue,
        cost: summary.invested,
        pnl: summary.unrealized + summary.realized,
        pnlPct: summary.invested > 0
          ? ((summary.unrealized + summary.realized) / summary.invested) * 100
          : 0,
        realizedPnl: summary.realized,
        positions: holdings.filter(h => !h._isFullyClosed).length,
      }));
    } catch { /* sessionStorage full or disabled — ignore */ }
  }, [demoId, portfolio, dataLoading, summary, holdings]);

  if (!portfolio) {
    return (
      <div style={s.page}>
        <p style={{ color: 'var(--text-secondary)' }}>Demo portfolio not found.</p>
        <Link to="/portfolios" style={s.backLink}>← Back to Portfolios</Link>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Sign-in prompt */}
      <div style={s.banner}>
        <div style={s.bannerText}>
          <strong>This is a demo portfolio.</strong>
          <span> Sign in to create and track your own holdings.</span>
        </div>
        <div style={s.bannerActions}>
          <Link to="/login"    style={s.btnPrimary}>Sign in</Link>
          <Link to="/register" style={s.btnOutline}>Create account</Link>
        </div>
      </div>

      {/* Title */}
      <div style={s.titleRow}>
        <div>
          <div style={s.demoLabel}>DEMO</div>
          <h1 className="rp-portfolio-title" style={s.title}>{portfolio.name}</h1>
          <div style={s.meta}>Base Currency: {cur}</div>
        </div>
        <div style={s.currencyBadge}>{cur}</div>
      </div>

      {/* Performance chart — PortfolioChart has its own mobile layout */}
      <PortfolioChart
        points={points}
        loading={dataLoading}
        reportCurrency={cur}
        selectedRange={range}
        onChangeRange={setRange}
        allRangeLabel="Since buy"
        portfolioName={portfolio.name}
        benchmarkPoints={benchmarkPoints}
        transactions={transactions}
        benchmarkTicker={BENCHMARK_TICKER}
        benchmarkLoading={dataLoading}
        benchmarkError={false}
        walletOn={false}
        groupedHoldings={holdings}
        tickerColorMap={tickerColorMap}
      />

      {/* Hero metrics — 5 cards on one row (Total Value, Invested, Realized,
          Unrealized, All-time dividends) */}
      <PortfolioHero
        totalValue={summary.holdingsValue}
        holdingsValue={summary.holdingsValue}
        cashBalance={0}
        netContributions={summary.invested}
        realizedPnl={summary.realized}
        unrealizedPnl={summary.unrealized}
        totalPnl={summary.unrealized + summary.realized}
        dividendIncomeTtm={0}
        dividendIncomeAllTime={summary.dividendAllTime}
        yieldOnCostTtm={summary.yieldOnCost}
        dividendYieldTtm={summary.dividendYield}
        annualDividendForecast={summary.annualDiv}
        monthlyAvgIncomeTtm={summary.annualDiv / 12}
        xirr={null}
        reportCurrency={cur}
        walletEnabled={false}
        visibleMetricIds={['totalValue', 'invested', 'realizedPnl', 'unrealizedPnl', 'dividendIncomeAllTime']}
      />

      <p style={s.heroNote}>
        Showing a summary view.{' '}
        <Link to="/register" style={s.heroNoteLink}>Create a free account</Link>{' '}
        and your own portfolio to unlock detailed metrics — dividend income,
        yield on cost, XIRR, cash flow and more.
      </p>

      {/* Holdings — open positions */}
      <HoldingsTable
        loading={dataLoading}
        groupedHoldings={openHoldings}
        portfolioId={demoId}
        portfolioSlug=""
        reportCurrency={cur}
        isTransactionMode={true}
        defaultVisibleColumns={DEMO_OPEN_COLS}
        storageKey={null}
      />

      {/* Closed positions (separate table, like the authenticated dashboard) */}
      {closedHoldings.length > 0 && (
        <HoldingsTable
          loading={dataLoading}
          groupedHoldings={closedHoldings}
          portfolioId={demoId}
          portfolioSlug=""
          reportCurrency={cur}
          isTransactionMode={true}
          isClosed={true}
          title="Closed Positions"
          defaultVisibleColumns={DEMO_CLOSED_COLS}
          storageKey={null}
        />
      )}

      {/* Allocation — only open positions */}
      <PortfolioAllocationChart
        groupedHoldings={openHoldings}
        reportCurrency={cur}
        loading={dataLoading}
        portfolioId={demoId}
        tickerColorMap={tickerColorMap}
        currencyColors={{}}
        assetTypeColors={{}}
        dividendsByTicker={dividendsByTicker}
        showDividends={true}
      />

      {/* Transactions */}
      <HoldingTransactionsTable
        loading={false}
        transactions={transactions}
        title="Buy / Sell Transactions"
        reportCurrency={cur}
        priceByTicker={priceByTicker}
        storageKey={`demo-${demoId}`}
        onDeleteTransaction={noop}
        onUpdateTransaction={asyncNoop}
      />

      <p style={s.note}>
        Live prices, static sample holdings — sign in to build and track your own portfolio.
      </p>
    </div>
  );
}

const s = {
  page: { maxWidth: '1400px', width: '100%', margin: '0 auto', padding: '8px 4px 48px', boxSizing: 'border-box' },
  backLink: { color: 'var(--accent)', textDecoration: 'none', fontWeight: 700 },

  banner: {
    background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '14px',
    padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap',
  },
  bannerText: { fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 },
  bannerActions: { display: 'flex', gap: '10px', flexShrink: 0 },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', height: '36px', padding: '0 18px', borderRadius: '10px', background: 'var(--primary-gradient)', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '14px' },
  btnOutline: { display: 'inline-flex', alignItems: 'center', height: '36px', padding: '0 18px', borderRadius: '10px', background: 'transparent', border: '1.5px solid var(--primary)', color: 'var(--primary)', textDecoration: 'none', fontWeight: 700, fontSize: '14px' },

  titleRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '20px' },
  demoLabel: { display: 'inline-block', background: 'var(--accent)', color: '#fff', fontSize: '10px', fontWeight: 800, letterSpacing: '0.8px', borderRadius: '6px', padding: '3px 8px', marginBottom: '8px' },
  title: { margin: '0 0 4px', fontSize: '26px', fontWeight: 800, color: 'var(--text-primary)' },
  meta: { color: 'var(--text-secondary)', fontSize: '14px' },
  currencyBadge: { background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '8px', padding: '4px 10px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.5px' },

  note: { fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '20px' },
  heroNote: { fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '12px 2px 28px' },
  heroNoteLink: { color: 'var(--primary)', fontWeight: 700, textDecoration: 'none' },
};
